<template>
  <el-dialog
    v-model="dialogVisible"
    title=""
    width="700px"
    :show-close="false"
    :close-on-click-modal="false"
    modal-class="add-patient-dialog"
  >
    <!-- Custom Header -->
    <template #header>
      <div class="flex justify-between items-center px-6 py-4 bg-[#004879] text-white rounded-t-[10px]">
        <div class="flex items-center gap-1">
          <img src="@/assets/MyPatient/AddPatient_white.svg" alt="Patient" class="w-6 h-6" />
          <span class="font-semibold text-[20px] leading-normal text-white">Add New Patient</span>
        </div>
        <button class="bg-transparent border-none p-0 cursor-pointer w-7 h-7" @click="closeDialog">
          <img src="@/assets/MyPatient/close.svg" alt="Close" class="w-full h-full" />
        </button>
      </div>
    </template>

    <!-- Dialog Content -->
    <div class="p-6 bg-[#F9FBFD] max-h-[70vh] overflow-y-auto">
      <!-- Name Fields -->
      <div class="flex gap-4 mb-[10px]">
        <div class="flex-1">
          <label class="block font-normal text-[14px] leading-5 text-[#4a4a4a] mb-1" style="font-family: 'IBM Plex Sans Hebrew', sans-serif;">First Name <span class="text-[#ff4d4f] font-normal">(Required)</span></label>
          <el-input
            v-model="formData.firstName"
            placeholder="Patient's first name"
            class="form-input"
            size="large"
            maxlength="50"
            show-word-limit
          />
        </div>
        <div class="flex-1">
          <label class="block font-normal text-[14px] leading-5 text-[#4a4a4a] mb-1" style="font-family: 'IBM Plex Sans Hebrew', sans-serif;">Middle Name</label>
          <el-input
            v-model="formData.middleName"
            placeholder="Middle Name"
            class="form-input"
            size="large"
            maxlength="50"
            show-word-limit
          />
        </div>
        <div class="flex-1">
          <label class="block font-normal text-[14px] leading-5 text-[#4a4a4a] mb-1" style="font-family: 'IBM Plex Sans Hebrew', sans-serif;">Last Name <span class="text-[#ff4d4f] font-normal">(Required)</span></label>
          <el-input
            v-model="formData.lastName"
            placeholder="Patient's last name"
            class="form-input"
            size="large"
            maxlength="50"
            show-word-limit
          />
        </div>
      </div>

      <!-- Phone Number -->
      <div class="mb-[10px]">
        <label class="block font-normal text-[14px] leading-5 text-[#4a4a4a] mb-1" style="font-family: 'IBM Plex Sans Hebrew', sans-serif;">Phone Number <span class="text-[#ff4d4f] font-normal">(Required)</span></label>
        <div class="flex gap-2">
          <el-select v-model="formData.countryCode" class="country-select" size="large">
            <el-option label="🇺🇸  +1" :value="+1" />
          </el-select>
          <el-input
            v-model="formData.phoneNumber"
            placeholder="000-000-0000"
            class="phone-input"
            size="large"
          />
        </div>
      </div>

      <!-- Email Address -->
      <div class="mb-[10px]">
        <label class="block font-normal text-[14px] leading-5 text-[#4a4a4a] mb-1" style="font-family: 'IBM Plex Sans Hebrew', sans-serif;">Email Address</label>
        <el-input
          v-model="formData.email"
          placeholder="example@email.com"
          class="form-input"
          size="large"
          maxlength="100"
          show-word-limit
          :class="{ 'error-input': emailError }"
        />
        <div v-if="emailError" class="text-[#ff4d4f] text-[12px] mt-1">{{ emailError }}</div>
      </div>

      <!-- Date of Birth and Gender -->
      <div class="flex gap-4 mb-[10px]">
        <div class="flex-1">
          <label class="block font-normal text-[14px] leading-5 text-[#4a4a4a] mb-1" style="font-family: 'IBM Plex Sans Hebrew', sans-serif;">Date of Birth</label>
          <el-input
            v-model="formData.dateOfBirth"
            placeholder="MM-DD_YYYY"
            class="form-input"
            size="large"
          />
        </div>
        <div class="flex-1">
          <label class="block font-normal text-[14px] leading-5 text-[#4a4a4a] mb-1" style="font-family: 'IBM Plex Sans Hebrew', sans-serif;">Bio Gender</label>
          <el-input
            v-model="formData.bioGender"
            placeholder="Bio gender"
            class="form-input"
            size="large"
          />
        </div>
      </div>

      <!-- Street Address -->
      <div class="mb-[10px]">
        <label class="block font-normal text-[14px] leading-5 text-[#4a4a4a] mb-1" style="font-family: 'IBM Plex Sans Hebrew', sans-serif;">Street Address</label>
        <el-input
          v-model="formData.streetAddress"
          placeholder="Street Address"
          class="form-input"
          size="large"
        />
      </div>

      <!-- City, State, Zip Code -->
      <div class="flex gap-4 mb-[10px]">
        <div class="flex-1">
          <label class="block font-normal text-[14px] leading-5 text-[#4a4a4a] mb-1" style="font-family: 'IBM Plex Sans Hebrew', sans-serif;">City</label>
          <el-input
            v-model="formData.city"
            placeholder="City"
            class="form-input"
            size="large"
          />
        </div>
        <div class="flex-1">
          <label class="block font-normal text-[14px] leading-5 text-[#4a4a4a] mb-1" style="font-family: 'IBM Plex Sans Hebrew', sans-serif;">State</label>
          <el-input
            v-model="formData.state"
            placeholder="State / Region"
            class="form-input"
            size="large"
          />
        </div>
        <div class="flex-1">
          <label class="block font-normal text-[14px] leading-5 text-[#4a4a4a] mb-1" style="font-family: 'IBM Plex Sans Hebrew', sans-serif;">Zip Code</label>
          <el-input
            v-model="formData.zipCode"
            placeholder="Zip Code"
            class="form-input"
            size="large"
          />
        </div>
      </div>
    </div>

    <!-- Dialog Footer -->
    <template #footer>
      <div class="flex justify-between gap-2.5 px-6 py-4 bg-[#F9FBFD]">
        <el-button class="cancel-btn" @click="closeDialog">Cancel</el-button>
        <el-button 
          type="primary" 
          class="save-btn" 
          :disabled="!isFormValid"
          @click="savePatient"
        >
          <span class="text-[14px] leading-5 tracking-[0.1px]" style="font-family: 'IBM Plex Sans Hebrew', sans-serif;">Save</span>
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script>
export default {
  name: 'AddPatientDialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      formData: {
        firstName: '',
        middleName: '',
        lastName: '',
        dateOfBirth: '',
        bioGender: '',
        email: '',
        countryCode: '+1',
        phoneNumber: '',
        streetAddress: '',
        city: '',
        state: '',
        zipCode: ''
      },
      emailError: ''
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) {
          this.$emit('close');
        }
      }
    },
    isFormValid() {
      const hasRequiredFields = this.formData.firstName.trim() && this.formData.lastName.trim();
      const emailValid = !this.formData.email || !this.emailError;
      return hasRequiredFields && emailValid;
    }
  },
  watch: {
    'formData.email'(newValue) {
      this.validateEmail(newValue);
    }
  },
  methods: {
    validateEmail(email) {
      if (!email) {
        this.emailError = '';
        return;
      }
      
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        this.emailError = 'Please enter a valid email address';
      } else {
        this.emailError = '';
      }
    },
    closeDialog() {
      this.resetForm();
      this.$emit('close');
    },
    savePatient() {
      if (this.isFormValid) {
        this.$emit('add', { ...this.formData });
        this.resetForm();
        this.$emit('close');
      }
    },
    resetForm() {
      this.formData = {
        firstName: '',
        middleName: '',
        lastName: '',
        dateOfBirth: '',
        bioGender: '',
        email: '',
        countryCode: '+1',
        phoneNumber: '',
        streetAddress: '',
        city: '',
        state: '',
        zipCode: ''
      };
      this.emailError = '';
    }
  }
}
</script>

<style>
.add-patient-dialog .el-dialog {
  border-radius: 10px;
  overflow: hidden;
  background-color: #F9FBFD;
  --el-dialog-padding-primary: 0px !important;
}
.add-patient-dialog.el-dialog__header {
  padding: 0;
  margin: 0;
}

.add-patient-dialog .el-dialog__body {
  padding: 0;
}

.add-patient-dialog .el-dialog__footer {
  padding: 0;
}

.add-patient-dialog {
  border-radius: 12px;
  overflow: hidden;
}
</style>

<style scoped>
.country-select {
  width: 120px;
  flex-shrink: 0;
}

.phone-input {
  flex: 1;
}

.country-option {
  display: flex;
  align-items: center;
  gap: 6px;
}

.flag-icon {
  width: 20px;
  height: 15px;
}

.form-input {
  width: 100%;
}

.cancel-btn {
  width: 80px;
  height: 32px;
  border: 1px solid #d10000;
  color: #d10000;
  background-color: white;
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-weight: 400;
  font-size: 14px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.cancel-btn:hover {
  border-color: #d10000;
  color: #d10000;
}

.save-btn {
  min-width: 80px;
  height: 32px;
  background-color: #b0b0b0;
  border-color: #b0b0b0;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.save-btn:not(:disabled) {
  background-color: #004879;
  border-color: #004879;
}

.save-btn:not(:disabled):hover {
  background-color: #0066a3;
  border-color: #0066a3;
}

/* Custom Element Plus styling */
:deep(.el-input .el-input__wrapper) {
  border-radius: 6px;
  border: 1px solid #d0d0d0;
  min-height: 32px;
  box-shadow: none;
}

:deep(.el-input .el-input__wrapper:hover) {
  border-color: #1890ff;
}

:deep(.el-input .el-input__wrapper.is-focus) {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

:deep(.el-select .el-input .el-input__wrapper) {
  border-radius: 6px;
  border: 1px solid #d0d0d0;
  min-height: 32px;
  box-shadow: none;
}

:deep(.el-select .el-input .el-input__wrapper:hover) {
  border-color: #1890ff;
}

:deep(.el-select .el-input .el-input__wrapper.is-focus) {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

:deep(.el-input__inner) {
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-size: 14px;
  color: #4a4a4a;
}

:deep(.el-select .el-input__inner) {
  font-family: 'IBM Plex Sans Hebrew', sans-serif;
  font-size: 14px;
  color: #4a4a4a;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
  }
  
  .phone-input-group {
    flex-direction: column;
  }
  
  .country-select {
    width: 100%;
  }
}
</style>
